import { 
  doc, 
  collection,
  addDoc, 
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  Firestore,
  DocumentReference,
  serverTimestamp,
  deleteField
} from "firebase/firestore";
import { toast } from "sonner";
import { Offer } from "@/types/offer";
import { offerConverter } from "./offerConverter";
import { prepareOfferData } from "./offerDataPreparation";
import { staffEditLandsOwnPrice, withSessionFactEdits } from "@/functions-shared/offer/storedSessionPrice";
import { withSessionWindowEdit } from "@/functions-shared/offer/storedSessionWindow";

// Get a single offer by ID
export const getCampOffer = async (db: Firestore, offerId: string): Promise<Offer | null> => {
  try {
    console.log(`Fetching offer with ID: ${offerId}`);
    const offerRef = doc(db, "offers", offerId).withConverter(offerConverter);
    const offerSnap = await getDoc(offerRef);
    
    if (offerSnap.exists()) {
      const offerData = offerSnap.data();
      console.log(`Successfully retrieved offer: ${offerId}`);
      return offerData;
    } else {
      console.log(`Offer not found: ${offerId}`);
      return null;
    }
  } catch (error) {
    console.error(`Error fetching offer ${offerId}:`, error);
    throw error;
  }
};

// Get offers by location ID
export const getOffersByLocationId = async (db: Firestore, locationId: string): Promise<Offer[]> => {
  try {
    console.log(`Fetching offers for location: ${locationId}`);
    const offersRef = collection(db, "offers");
    const locationRef = doc(db, "campLocations", locationId);
    
    const q = query(
      offersRef,
      where("locationRef", "==", locationRef)
    );
    
    const querySnapshot = await getDocs(q.withConverter(offerConverter));
    const offers = querySnapshot.docs.map(doc => doc.data());
    
    console.log(`Retrieved ${offers.length} offers for location ${locationId}`);
    return offers;
  } catch (error) {
    console.error("Error fetching offers:", error);
    return [];
  }
};

const FIRESTORE_IN_QUERY_LIMIT = 30;

/** Resolve `campLocations` document id from an offer's `locationRef`. */
export function campLocationIdFromOfferLocationRef(ref: Offer["locationRef"] | undefined | null): string | null {
  if (ref == null) return null;
  if (typeof ref === "string") {
    const parts = ref.split("/").filter(Boolean);
    return parts[parts.length - 1] || null;
  }
  if (typeof (ref as DocumentReference).id === "string") {
    return (ref as DocumentReference).id;
  }
  const path = (ref as { path?: string }).path;
  if (typeof path === "string") {
    const parts = path.split("/").filter(Boolean);
    return parts[parts.length - 1] || null;
  }
  return null;
}

/**
 * Load offers for many locations using chunked `locationRef in (...)` queries (30 refs per request).
 */
export async function getOffersGroupedByCampLocationId(
  db: Firestore,
  locationIds: string[]
): Promise<Map<string, Offer[]>> {
  const map = new Map<string, Offer[]>();
  const uniqueIds = [...new Set(locationIds.filter(Boolean))];
  for (const id of uniqueIds) {
    map.set(id, []);
  }
  for (let i = 0; i < uniqueIds.length; i += FIRESTORE_IN_QUERY_LIMIT) {
    const chunk = uniqueIds.slice(i, i + FIRESTORE_IN_QUERY_LIMIT);
    const refs = chunk.map((id) => doc(db, "campLocations", id));
    const q = query(collection(db, "offers"), where("locationRef", "in", refs));
    const snap = await getDocs(q.withConverter(offerConverter));
    for (const d of snap.docs) {
      const offer = d.data();
      const locId = campLocationIdFromOfferLocationRef(offer.locationRef);
      if (locId && map.has(locId)) {
        map.get(locId)!.push(offer);
      }
    }
  }
  return map;
}

// Create a new offer
export const createOffer = async (
  db: Firestore, 
  locationId: string, 
  offerData: Partial<Offer>, 
  userId: string
): Promise<Offer | null> => {
  try {
    console.log(`Creating new offer for location: ${locationId}, userId: ${userId}`);
    
    // Validate required IDs
    if (!locationId) {
      console.error("Location ID is required to create an offer");
      throw new Error("Location ID is required to create an offer");
    }
    
    if (!userId) {
      console.error("User ID is required to create an offer");
      throw new Error("User ID is required to create an offer");
    }
    
    // Get a reference to the offers collection
    const offersRef = collection(db, "offers");
    
    // If numPlaceholderOffers not provided, copy from location.placeholderOffersCount
    const offerDataWithCount = { ...offerData } as any;
    if (offerDataWithCount.numPlaceholderOffers === undefined) {
      const locSnap = await getDoc(doc(db, "campLocations", locationId));
      const locData: any = locSnap.exists() ? locSnap.data() : {};
      if (typeof locData.placeholderOffersCount === 'number') {
        offerDataWithCount.numPlaceholderOffers = locData.placeholderOffersCount;
      } else {
        offerDataWithCount.numPlaceholderOffers = null;
      }
    }

    // Prepare the complete offer data
    // KB-3733 — a create: no stored session price to keep.
    const newOfferData = await prepareOfferData(db, locationId, offerDataWithCount, userId, null);

    // Set avStatusAsOf on create
    (newOfferData as any).avStatusAsOf = serverTimestamp();
    
    // Ensure we have no undefined values that would cause Firestore errors
    Object.entries(newOfferData).forEach(([key, value]) => {
      if (value === undefined) {
        console.log(`[createOffer] Converting undefined ${key} to null`);
        (newOfferData as any)[key] = null;
      }
    });
    
    // Log the final prepared data for debugging
    console.log(`Prepared offer data for Firestore:`, {
      variationKey: newOfferData.variationKey,
      createdById: userId,
      createdByRef: typeof newOfferData.createdBy === 'object' 
        ? (newOfferData.createdBy as DocumentReference).path 
        : newOfferData.createdBy,
      lastEditedByRef: typeof newOfferData.lastEditedBy === 'object' 
        ? (newOfferData.lastEditedBy as DocumentReference).path 
        : newOfferData.lastEditedBy,
      locationRef: typeof newOfferData.locationRef === 'object' 
        ? (newOfferData.locationRef as DocumentReference).path 
        : newOfferData.locationRef,
      priceOverride: newOfferData.priceOverride,
      priceOverrideType: typeof newOfferData.priceOverride,
      numDaysOverride: newOfferData.numDaysOverride,
      numDaysOverrideType: typeof newOfferData.numDaysOverride 
    });
    
    // Add the new offer to Firestore and let Firestore generate the ID
    const docRef = await addDoc(offersRef, newOfferData);
    
    console.log(`Successfully created offer with ID: ${docRef.id}`);
    
    // Get the created offer with its ID
    const createdOffer = {
      id: docRef.id,
      ...newOfferData,
    } as unknown as Offer;
    
    return createdOffer;
  } catch (error) {
    console.error("Error creating offer:", error);
    // Log more specific error information
    if (error instanceof Error) {
      console.error(`Error type: ${error.name}, Message: ${error.message}`);
      if ('code' in error) {
        console.error(`Error code: ${(error as any).code}`);
      }
      console.error(`Stack trace: ${error.stack}`);
      if ((error as any).code === 'permission-denied') {
        console.error("CRITICAL: Permission denied error when creating offer. Check Firestore security rules.");
      }
    }
    throw error;
  }
};

// Update an existing offer
export const updateOffer = async (
  db: Firestore, 
  offerId: string, 
  offerData: Partial<Offer>,
  userId: string
): Promise<Offer | null> => {
  try {
    console.log(`Updating offer with ID: ${offerId}`);
    const offerRef = doc(db, "offers", offerId);
    
    // Get the current offer to get its locationRef
    const currentOfferSnap = await getDoc(offerRef);
    if (!currentOfferSnap.exists()) {
      throw new Error(`Offer ${offerId} not found`);
    }
    
    const currentOffer = currentOfferSnap.data() as Offer;
    
    // Get locationId from the reference
    const locationRef = currentOffer.locationRef;
    let locationId = '';
    
    if (typeof locationRef === 'string') {
      // If it's a string path like "campLocations/id"
      const parts = locationRef.split('/');
      locationId = parts[parts.length - 1];
    } else if (locationRef?.id) {
      // If it's a DocumentReference
      locationId = locationRef.id;
    } else {
      throw new Error(`Invalid locationRef in offer ${offerId}`);
    }
    
    console.log(`Updating offer ${offerId} with locationId: ${locationId}, userId: ${userId}`);
    
    // Check for undefined values and convert to null
    Object.entries(offerData).forEach(([key, value]) => {
      if (value === undefined) {
        console.log(`[updateOffer] Converting undefined ${key} to null`);
        (offerData as any)[key] = null;
      }
    });
    
    // Determine whether avStatusAsOf should be refreshed
    const hasAvailabilityInUpdate = Object.prototype.hasOwnProperty.call(offerData, 'availabilityStatus');
    const nextAvailability = hasAvailabilityInUpdate ? (offerData as any).availabilityStatus : undefined;
    // Only refresh avStatusAsOf when the availability value actually changes
    const shouldRefreshAvStatusAsOf = hasAvailabilityInUpdate && (nextAvailability !== currentOffer.availabilityStatus);

    // Prepare the updated offer data
    const merged = { ...currentOffer, ...offerData };
    const fullUpdateData = await prepareOfferData(
      db,
      locationId,
      merged,
      userId,
      // KB-3733 — any single-offer edit rebuilds the whole doc; a camp-data session keeps its own price
      // and length, read from the PRE-edit doc, with any session fact the edit explicitly sets laid over it
      // (EntityHistoryPanel restores a field through here). KB-4564 — likewise the window: the PRE-edit doc's,
      // unless the edit sends `variationData.timings` (a restore, or an Inbox accept of a variationData proposal).
      withSessionWindowEdit(
        withSessionFactEdits(currentOffer as unknown as Record<string, unknown>, offerData as Record<string, unknown>),
        offerData as Record<string, unknown>
      )
    );
    
    // Strip id from the update data
    const { id, ...updateData } = fullUpdateData;
    
    // Remove avStatusAsOf from update payload unless we explicitly refresh it
    delete (updateData as any).avStatusAsOf;

    // Ensure we have no undefined values that would cause Firestore errors
    Object.entries(updateData).forEach(([key, value]) => {
      if (value === undefined) {
        // Skip avStatusAsOf entirely; we never want to write null to this field implicitly
        if (key === 'avStatusAsOf') return;
        console.log(`[updateOffer] Converting undefined ${key} to null before sending to Firestore`);
        (updateData as any)[key] = null;
      }
    });

    // Conditionally set avStatusAsOf only when availability actually changed
    if (shouldRefreshAvStatusAsOf) {
      (updateData as any).avStatusAsOf = serverTimestamp();
    }

    // KB-3769 — an explicit list-price edit on a program-default camp-data session makes that price its own; the
    // `priceSource` label goes in the same write, or the next rebuild would refresh the edit away (rule 2).
    if (staffEditLandsOwnPrice(currentOffer as unknown as Record<string, unknown>, offerData as Record<string, unknown>)) {
      (updateData as any).priceSource = deleteField();
    }
    
    // Log critical fields for debugging
    console.log(`[updateOffer] Critical fields in update data:`, {
      priceOverride: updateData.priceOverride,
      priceOverrideType: typeof updateData.priceOverride,
      numDaysOverride: updateData.numDaysOverride,
      numDaysOverrideType: typeof updateData.numDaysOverride,
      createdByRef: typeof updateData.createdBy === 'object' 
        ? (updateData.createdBy as DocumentReference).path 
        : updateData.createdBy,
      lastEditedByRef: typeof updateData.lastEditedBy === 'object' 
        ? (updateData.lastEditedBy as DocumentReference).path 
        : updateData.lastEditedBy
    });
    
    // Update the offer in Firestore
    await updateDoc(offerRef, updateData);
    
    console.log(`Successfully updated offer: ${offerId}`);
    
    // Get the updated offer
    // In production, DocumentReference has withConverter; some tests use simple mocks without it.
    const anyOfferRef: any = offerRef as any;
    if (typeof anyOfferRef.withConverter === 'function') {
      const updatedOfferSnap = await getDoc(anyOfferRef.withConverter(offerConverter));
      if (updatedOfferSnap.exists()) {
        return updatedOfferSnap.data() as Offer;
      } else {
        console.error(`Offer ${offerId} not found after update`);
        return null;
      }
    }
    // Fallback for test environments where withConverter is not available on the mock
    // Return the merged view of the current and updated data to approximate the fresh read
    return { ...(currentOffer as any), ...(updateData as any), id: offerId } as unknown as Offer;
  } catch (error) {
    console.error(`Error updating offer ${offerId}:`, error);
    // Log more specific error information
    if (error instanceof Error) {
      console.error(`Error type: ${error.name}, Message: ${error.message}`);
      if ('code' in error) {
        console.error(`Error code: ${(error as any).code}`);
      }
      console.error(`Stack trace: ${error.stack}`);
      if ((error as any).code === 'permission-denied') {
        console.error("CRITICAL: Permission denied error when updating offer. Check Firestore security rules.");
      }
    }
    throw error;
  }
};

// Delete an offer by ID - Fixed with proper error handling and logging
export const deleteOffer = async (db: Firestore, offerId: string): Promise<boolean> => {
  if (!offerId) {
    console.error("DEBUG - deleteOffer - No offer ID provided");
    throw new Error("Cannot delete: No offer ID provided");
  }
  
  try {
    console.log(`DEBUG - deleteOffer - Attempting to delete offer with ID: ${offerId}`);
    
    // Get a reference to the document
    const offerRef = doc(db, "offers", offerId);
    
    // Verify the document exists before attempting deletion
    const docSnapshot = await getDoc(offerRef);
    if (!docSnapshot.exists()) {
      console.error(`DEBUG - deleteOffer - Offer with ID ${offerId} does not exist`);
      throw new Error(`Offer with ID ${offerId} does not exist`);
    }
    
    // Delete the document
    await deleteDoc(offerRef);
    console.log(`DEBUG - deleteOffer - Successfully deleted offer with ID: ${offerId}`);
    return true;
  } catch (error) {
    console.error(`DEBUG - deleteOffer - Error deleting offer with ID ${offerId}:`, error);
    if (error instanceof Error) {
      console.error(`Error details: ${error.message}`, {
        name: error.name,
        stack: error.stack,
      });
    }
    throw error;
  }
};
